import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { WebhooksApiAdapter } from '../adapters/WebhooksApiAdapter';
import { CreateEndpointPayload, UpdateEndpointPayload } from '../models/Webhooks';
import { WEBHOOK_ENDPOINTS_QUERY_KEY } from './useWebhookEndpoints';

export const useWebhookMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('webhooksModule');

  const httpClient = createHttpClient(apiClientWH);
  const adapter = new WebhooksApiAdapter(httpClient);

  const invalidateEndpoints = () => {
    queryClient.invalidateQueries({ queryKey: [WEBHOOK_ENDPOINTS_QUERY_KEY] });
  };

  const createEndpoint = useMutation({
    mutationFn: (payload: CreateEndpointPayload) => adapter.createEndpoint(payload),
    onSuccess: () => {
      invalidateEndpoints();
      toast({ title: t('toast.created'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  const updateEndpoint = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateEndpointPayload }) =>
      adapter.updateEndpoint(id, payload),
    onSuccess: () => {
      invalidateEndpoints();
      toast({ title: t('toast.updated'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteEndpoint = useMutation({
    mutationFn: (id: number) => adapter.deleteEndpoint(id),
    onSuccess: () => {
      invalidateEndpoints();
      toast({ title: t('toast.deleted'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  const rotateSecret = useMutation({
    mutationFn: (id: number) => adapter.rotateSecret(id),
    onSuccess: () => {
      invalidateEndpoints();
      toast({ title: t('toast.secretRotated'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  const testEndpoint = useMutation({
    mutationFn: (id: number) => adapter.testEndpoint(id),
    onSuccess: () => {
      toast({ title: t('toast.testSent'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  const retryDelivery = useMutation({
    mutationFn: (deliveryId: number) => adapter.retryDelivery(deliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
      toast({ title: t('toast.retryStarted'), variant: 'default' });
    },
    onError: (error: any) => {
      toast({
        title: t('toast.errorGeneric'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createEndpoint,
    updateEndpoint,
    deleteEndpoint,
    rotateSecret,
    testEndpoint,
    retryDelivery,
  };
};
